const TRANSLATION_PREFIX = 'STATIC.PAGES.DATA_SOURCES.YF_DATA_STATUS';

export const yfTranslationKey = suffix => `${TRANSLATION_PREFIX}.${suffix}`;

// Mirrors AuditStatus in dashboard/adnets/audit/model.py.
export const AUDIT_STATUS = {
    SYNCED: 'synced',
    UNSYNCED: 'unsynced',
    NOT_CALCULATED: 'not_calculated',
    DISABLED: 'disabled',
    UNAVAILABLE: 'unavailable',
};

export const SLOW_PULL_MS = 45000;

export const formatElapsed = elapsedMs => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

export const AUDIT_METRICS = ['costs', 'installs', 'clicks', 'impressions'];

// Keep in sync with DELTA_TOLERANCE_PERCENT in dashboard/adnets/audit/consts.py.
const DELTA_TOLERANCE_PERCENT = 1;

export const formatMetricValue = value => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (Math.abs(value) >= 10000) {
        return Math.round(value)
            .toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 })
            .toUpperCase();
    }
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
};

export const formatExactValue = value => {
    if (value === null || value === undefined) {
        return '—';
    }
    return Number.isInteger(value)
        ? value.toLocaleString()
        : value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          });
};

const _reported = value => value !== null && value !== undefined;

// Mirrors AuditDelta.agrees in dashboard/adnets/audit/model.py. `short` = Singular has less.
export const formatDelta = ({ adnet_value: adnetValue, singular_value: singularValue, percent }) => {
    if (!_reported(percent)) {
        if (!adnetValue && !singularValue) {
            return { label: _reported(adnetValue) && _reported(singularValue) ? '0%' : '—', tone: 'ok' };
        }
        if (adnetValue) {
            return { label: '-100%', tone: 'short' };
        }
        return { label: `+${formatMetricValue(singularValue)}`, tone: 'over' };
    }

    const rounded = Number(percent.toFixed(1));
    if (rounded === 0) {
        return { label: percent === 0 ? '0%' : '~0%', tone: 'ok' };
    }

    const label = `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}%`;

    if (Math.abs(percent) <= DELTA_TOLERANCE_PERCENT) {
        return { label, tone: 'ok' };
    }
    return { label, tone: percent < 0 ? 'short' : 'over' };
};

// An absent Singular side reads 0 when the ad network did report.
export const metricDisplayValue = ({ adnet_value: adnetValue, singular_value: singularValue }) => {
    if (_reported(singularValue)) {
        return formatMetricValue(singularValue);
    }
    return _reported(adnetValue) ? '0' : '—';
};

export const hasDeltaChip = delta => ['short', 'over'].includes(formatDelta(delta).tone);

export const hasAdnetSide = deltas => AUDIT_METRICS.some(metric => _reported((deltas[metric] || {}).adnet_value));

export const ROW_GROUP = {
    UNAVAILABLE: 'unavailable',
    MISSING: 'missing',
    GAP: 'gap',
    UNCHECKED: 'unchecked',
    MATCHED: 'matched',
    DISABLED: 'disabled',
};

// UNSYNCED splits into MISSING (Singular short) and GAP (Singular over).
export const rowGroup = row => {
    if (row.status === AUDIT_STATUS.DISABLED) {
        return ROW_GROUP.DISABLED;
    }
    if (row.status === AUDIT_STATUS.UNAVAILABLE) {
        return ROW_GROUP.UNAVAILABLE;
    }
    if (row.status === AUDIT_STATUS.SYNCED) {
        return ROW_GROUP.MATCHED;
    }
    if (row.status === AUDIT_STATUS.UNSYNCED) {
        const isShort = AUDIT_METRICS.some(metric => formatDelta(row.deltas[metric] || {}).tone === 'short');
        return isShort ? ROW_GROUP.MISSING : ROW_GROUP.GAP;
    }
    return ROW_GROUP.UNCHECKED;
};

export const groupRows = rows =>
    rows.reduce((groups, row) => {
        const group = rowGroup(row);
        groups[group] = groups[group] || [];
        groups[group].push(row);
        return groups;
    }, {});

export const latestLoad = connector => (connector.loads || [])[0] || {};

export const formatCheckTime = (utcTimestamp, timeZone = undefined) => {
    if (!utcTimestamp) {
        return '';
    }
    return new Date(`${utcTimestamp}Z`).toLocaleString(undefined, {
        timeZone,
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const formatAuditDay = isoDate => {
    if (!isoDate) {
        return '';
    }
    return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(undefined, {
        timeZone: 'UTC',
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};
